import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { fetchMeta, fetchProducts } from "@/lib/api";
import { Reveal, SplitLines } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { ProductModal } from "@/components/ProductModal";

const TABS = [{ key: "size", label: "By Size" }, { key: "surface", label: "By Surface" }, { key: "space", label: "By Space" }];

export const Collection = () => {
  const [tab, setTab] = useState("size");
  const [active, setActive] = useState(null);
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: fetchMeta });
  const { data: featured = [] } = useQuery({ queryKey: ["products", "featured"], queryFn: () => fetchProducts({ featured: true }) });

  const lists = { size: meta?.sizes || [], surface: meta?.surfaces || [], space: meta?.spaces || [] };

  return (
    <section id="collection" data-testid="collection-section" className="bg-[var(--bg-soft)] py-28 lg:py-36">
      <div className="container-x">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Reveal><p className="eyebrow mb-6">Our Products</p></Reveal>
            <SplitLines as="h2" inView className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.05] tracking-tight text-[var(--navy)]" lines={["Explore our", <>signature <em className="italic text-[var(--mint)]">tile collection.</em></>]} />
          </div>
          <Reveal delay={0.2}>
            <div className="flex gap-2 rounded-full bg-white p-1.5 border border-slate-200 w-fit" data-testid="collection-tabs">
              {TABS.map((t) => (
                <button key={t.key} data-testid={`collection-tab-${t.key}`} onClick={() => setTab(t.key)} className={`relative rounded-full px-5 py-2.5 text-xs font-semibold transition-colors ${tab === t.key ? "text-white" : "text-[var(--navy)] hover:text-[var(--mint)]"}`}>
                  {tab === t.key && <motion.span layoutId="tab-pill" className="absolute inset-0 rounded-full bg-[var(--navy)]" transition={{ type: "spring", stiffness: 350, damping: 30 }} />}
                  <span className="relative">{t.label}</span>
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mt-10 min-h-[64px]">
          <AnimatePresence mode="wait">
            <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }} className="flex flex-wrap gap-2" data-testid={`collection-chips-${tab}`}>
              {lists[tab].map((item) => (
                <Link key={item.slug} to={`/collection?${tab}=${item.slug}`} data-testid={`chip-${tab}-${item.slug}`} className="chip">
                  {item.label}{item.is_new && <span className="ml-2 h-1.5 w-1.5 rounded-full bg-[var(--mint)]" />}
                </Link>
              ))}
              <Link to="/collection" className="chip !border-dashed" data-testid="chip-view-all">View all <ArrowRight size={14} className="ml-1.5" /></Link>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-16 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4 lg:gap-7" data-testid="featured-grid">
          {featured.map((p, i) => <ProductCard key={p.slug} product={p} index={i} onOpen={setActive} />)}
        </div>

        <Reveal className="mt-14 flex justify-center">
          <Link to="/collection" data-testid="collection-view-all-btn" className="btn-pill btn-navy">View the full catalogue <ArrowUpRight size={16} className="arrow" /></Link>
        </Reveal>
      </div>
      <ProductModal product={active} onClose={() => setActive(null)} />
    </section>
  );
};
